import { isOrHandleAsExternalUrl } from "../../../helpers/link-helper";
import { TImage, TImageLoading, TLink } from "../../../types";
import { THeadingTypes } from "../../../types/heading-types";
import Heading from "../heading/heading";
import LinkWrapper from "../link-wrapper/link-wrapper";
import { LinkFake } from "../link/link";
import styles from "./card.module.less";
import CardTrailer from "../../../packages/trailer-rental/components/elements/card-trailer/card-trailer";
import { useTranslation } from "react-i18next";
import { SecondaryButtonLink } from "../button/button";

type TBaseCardWrapperLink = {
  children: React.ReactNode;
  link: TLink;
  ariaLabel?: string;
  isOutline?: boolean;
  isProductCard?: boolean;
};

const BaseCardWrapperLink = ({ children, link, ariaLabel, isOutline, isProductCard }: TBaseCardWrapperLink) => {
  return (
    <LinkWrapper
      ariaLabel={ariaLabel}
      className={[styles.wrapper, isOutline ? styles.outline : "", isProductCard ? styles.productCard : ""].join(" ")}
      url={link ? link.url : undefined}
      target={isOrHandleAsExternalUrl(link?.url, link?.target) ? "_blank" : "_self"}
      onPreNavigate={link?.onPreNavigate}
    >
      {children}
    </LinkWrapper>
  );
};

export type TProductCardBadge = "member" | "new";

type TCardPartProduct = {
  title: string;
  subtitle: string;
  image: TImage;
  link: TLink;
  loading?: TImageLoading;
  badge?: TProductCardBadge;
};

const CardPartProduct = ({ title, subtitle, image, loading = "lazy", badge = undefined, link }: TCardPartProduct) => {
  return (
    <div className={styles.productWrapper}>
      <div className={styles.productTextWrapper}>
        <Heading semanticType={"h3"} visualType={"h4"} className={[styles.productCardTitle, "mb-sm"].join(" ")}>
          {title}
        </Heading>
        <p className={[styles.productCardSubtitle, "body-base", "mb-md"].join(" ")}>{subtitle}</p>
      </div>
      <div className={[styles.productButtonWrapper, "mb-md"].join(" ")}>
        <SecondaryButtonLink
          onPreNavigate={link?.onPreNavigate}
          className={styles.link}
          label={link?.title}
          url={link?.url}
          iconRight={"ui-icon-arrow-right"}
        />
      </div>
      <div className={styles.productImageWrapper}>
        {badge === "member" && <div className={styles.memberBadge} />}
        {badge === "new" && <div className={styles.newBadge} />}
        <img className={styles.image} src={image?.url} alt={image?.alt} loading={loading} />
      </div>
    </div>
  );
};

type TCardPartLinkBottom = {
  label: string;
};

const CardPartLinkBottom = ({ label }: TCardPartLinkBottom) => {
  return (
    <div className={styles.linkWrapper}>
      <LinkFake.Primary className={styles.link} label={label} />
      <span className={[styles.linkIcon, "ui-icon-arrow-right"].join(" ")} />
    </div>
  );
};

export type TCardPartImage = {
  image: TImage;
  loading?: TImageLoading;
};

const CardPartImage = ({ image, loading = "lazy" }: TCardPartImage) => {
  return (
    <div className={[styles.imageWrapper, "mb-md"].join(" ")}>
      <img className={styles.image} src={image?.url} alt={image?.alt} loading={loading} />
    </div>
  );
};

export type TCardPartText = {
  date?: string;
  category?: string;
  title: string;
  semanticHeadingType?: THeadingTypes;
  text: string;
  link?: TLink;
};

const CardPartText = ({ date, category, title, semanticHeadingType = "h3", text, link }: TCardPartText) => {
  const { t } = useTranslation();
  const linkLabel = link && link.url ? (link.title ? link.title : t("READ_MORE")) : undefined;
  const shouldDisplayLink = linkLabel ? true : false;

  return (
    <>
      <div className={[styles.textWrapper, shouldDisplayLink ? "mb-md" : ""].join(" ")}>
        {(date || category) && (
          <div className={styles.categoryDateWrapper}>
            {date && <p className={[styles.date, "eyebrow"].join(" ")}>{date}</p>}
            {category && <p className={[styles.category, "eyebrow"].join(" ")}>{category}</p>}
          </div>
        )}
        {title && (
          <Heading semanticType={semanticHeadingType} visualType={"h5"} className={[styles.title, "mb-xs"].join(" ")}>
            {title}
          </Heading>
        )}
        {text && <p className={[styles.text, "body-base mb-0"].join(" ")}>{text}</p>}
      </div>
      {linkLabel && <CardPartLinkBottom label={linkLabel} />}
    </>
  );
};

export type TCardText = {
  date?: string;
  category?: string;
  title: string;
  text: string;
  link: TLink;
};

const CardText = ({ date, category, title, text, link }: TCardText) => {
  return (
    <BaseCardWrapperLink link={link}>
      <CardPartText date={date} category={category} title={title} text={text} link={link} />
    </BaseCardWrapperLink>
  );
};

const CardTextOutline = ({ date, category, title, text, link }: TCardText) => {
  return (
    <BaseCardWrapperLink link={link} isOutline={true}>
      <CardPartText date={date} category={category} title={title} text={text} link={link} />
    </BaseCardWrapperLink>
  );
};

export type TCardImage = TCardText & {
  image: TImage;
  imageLoading?: TImageLoading;
};

const CardImage = ({ date, category, title, text, link, image, imageLoading }: TCardImage) => {
  return (
    <BaseCardWrapperLink link={link}>
      <CardPartImage image={image} loading={imageLoading} />
      <CardPartText date={date} category={category} title={title} text={text} link={link} />
    </BaseCardWrapperLink>
  );
};

export type TCardStandard = TCardImage & {
  isOutline?: boolean;
};

const CardStandard = ({ date, category, title, text, link, isOutline, image, imageLoading }: TCardStandard) => {
  return (
    <BaseCardWrapperLink link={link} isOutline={isOutline}>
      {image && <CardPartImage image={image} loading={imageLoading} />}
      <CardPartText date={date} category={category} title={title} text={text} link={link} />
    </BaseCardWrapperLink>
  );
};

export type TCardProduct = TCardPartProduct & {
  link: TLink;
};

const CardProduct = ({ title, subtitle, image, loading, badge, link }: TCardProduct) => {
  return (
    <CardPartProduct title={title} subtitle={subtitle} image={image} loading={loading} badge={badge} link={link} />
  );
};

export const Card = {
  Text: CardText,
  TextOutline: CardTextOutline,
  Image: CardImage,
  Standard: CardStandard,
  Product: CardProduct,
  Trailer: CardTrailer,
};
